import { useList } from '@refinedev/core';
import { Card, Skeleton } from 'antd';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { RESOURCES } from '@/Resources';

import ServiceOrderLoadedItems from './ServiceOrderLoadedItems';
import ServiceOrderShowAdmin from './ServiceOrderShowAdmin';
import ServiceOrderShowBilling from './ServiceOrderShowBilling';
import ServiceOrderShowDetail from './ServiceOrderShowDetail';
import ServiceOrderShowWorkDetail from './ServiceOrderShowWorkDetail';

interface ServiceOrderShowServiceInfoProps {
  serviceOrder: ServiceOrder;
  serviceOrderItems: ServiceOrderItem[];
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  modifingAdminData: boolean;
  setModifingAdminData: (modifying: boolean) => void;
  refetch: () => Promise<void>;
}

const ServiceOrderShowServiceInfo = (props: ServiceOrderShowServiceInfoProps) => {
  const { t } = useTranslation();
  const {
    serviceOrder,
    serviceOrderItems,
    setServiceOrderItems,
    modifingAdminData,
    setModifingAdminData,
    refetch,
  } = props;
  /**
   * Condiciones de visualización
   */
  const coordinationPending = useMemo(
    () => serviceOrder?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION,
    [serviceOrder?.status],
  );
  const showAdminData = useMemo(
    () =>
      serviceOrder?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION ||
      serviceOrder?.status == SERVICE_ORDER_STATUS.FINALIZADA,
    [serviceOrder?.status],
  );
  const {
    result: receptionsResult,
    query: { isLoading: isLoadingReceptions },
  } = useList<Reception>({
    resource: RESOURCES.RECEPTIONS,
    pagination: { pageSize: 1000 },
    queryOptions: {
      enabled: serviceOrder?.id != undefined,
    },
    meta: {
      created_by_service_order: serviceOrder?.id,
    },
  });

  return (
    <>
      <ServiceOrderShowDetail serviceOrder={serviceOrder} />
      <ServiceOrderShowWorkDetail serviceOrder={serviceOrder} />

      {!coordinationPending && (
        <>
          <ServiceOrderShowBilling serviceOrder={serviceOrder} />
          {showAdminData && (
            <ServiceOrderShowAdmin
              serviceOrder={serviceOrder}
              refetch={refetch}
              modifingAdminData={modifingAdminData}
              setModifingAdminData={setModifingAdminData}
            />
          )}

          <div className="ServiceOrderShowTitle">Elementos utilizados en el servicio</div>
          <ServiceOrderLoadedItems
            serviceOrderId={serviceOrder.id}
            serviceOrderItems={serviceOrderItems}
            setServiceOrderItems={setServiceOrderItems}
          />
          {isLoadingReceptions ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : receptionsResult?.data && receptionsResult.data.length > 0 ? (
            <Card className="ServiceOrderShowSection">
              <div className="ServiceOrderShowTitle">{t('Recepciones asociadas')}</div>

              <ul>
                {receptionsResult.data.map(reception => (
                  <li key={reception.id}>
                    <div className="ServiceOrderShowReceptionRow">
                      <LinkTextField
                        local
                        to={`/${RESOURCES.RECEPTIONS}/show/${reception.id}`}
                        label={`${t('Recepción')} #${reception.id}`}
                      />
                      {reception.status != undefined && (
                        <PackageStateTag packageStatus={reception.status} />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}
    </>
  );
};

export default ServiceOrderShowServiceInfo;
